import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { BrandConfig } from './brands';
import { detectBrand } from './brands';

const BrandContext = createContext<BrandConfig | undefined>(undefined);

export const useBrand = (): BrandConfig => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within BrandProvider');
  }
  return context;
};

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brand = useMemo(() => detectBrand(), []);

  useEffect(() => {
    const root = document.documentElement;
    const { colors } = brand;

    // Apply all brand CSS custom properties to :root
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-dark', colors.primaryDark);
    root.style.setProperty('--color-primary-darker', colors.primaryDarker);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-active', colors.active);
    root.style.setProperty('--color-button', colors.button);
    root.style.setProperty('--color-brand-dark', colors.brandDark);
    root.style.setProperty('--color-brand-dark-alpha', colors.brandDarkAlpha);
    root.style.setProperty('--color-header-accent', colors.headerAccent);

    // Update favicon
    const link: HTMLLinkElement =
      document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = brand.favicon;
    document.head.appendChild(link);

    // Update page title
    document.title = brand.pageTitle;
  }, [brand]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
};
